import React from "react"
import { BuildingStorefrontIcon } from "@heroicons/react/24/outline"
import { getValue } from "aldoo-ra/Value"
import useT from "../hooks/useT"
import { formatMoney } from "../account/ui"
import { groupBySeller, lineProduct, productPlatforms, platformId, totalLicenses } from "./cartItems"
import { useCurrency } from "../store/currency"

/** "iOS × 2 · Android × 1" for a license line */
export function licenseSummary(line, t) {
  const product = lineProduct(line)
  const distribution = line.options?.platform
  if (!Array.isArray(distribution)) return null
  const platforms = productPlatforms(product)
  if (!distribution.length) return `${totalLicenses(line)} ${totalLicenses(line) === 1 ? t`license` : t`licenses`}`
  return distribution
    .map((d) => {
      const entry = platforms.find((p) => platformId(p) === d.platformId)
      const name = entry?.platform?.name || entry?.name || d.platformId
      return `${name} × ${d.quantity}`
    })
    .join(" · ")
}

/**
 * Compact recap of an order. It used to sit above the payment form (the
 * "checkout-form-header" fragment) and is no longer registered there: the
 * payment step shows the card and the total, and nothing that was already
 * agreed to in the steps before it. `licenseSummary` below is still used by
 * the cart product line.:
 * item count, merchant, shipping, total and the delivery address. The items
 * themselves stay in the cart panel — the payment step must not grow with the
 * cart.
 */
export default function PaymentSummary({ store }) {
  const t = useT()
  const cart = store.cart
  const paymentInfo = store.paymentInfo
  const { currency: cartIn } = useCurrency()
  const currency = (paymentInfo?.currency || cartIn).toLowerCase()
  const shippingExtra = (store.cartExtras || []).find((e) => e.id === "shipping")
  const shippingCents = Math.round((shippingExtra?.amount || 0) * 100)
  const subtotalCents = Math.round(store.getCartSubtotal() * 100)
  const totalCents = paymentInfo?.amount ?? subtotalCents + shippingCents
  const groups = groupBySeller(cart)
  // Data collected by the flow steps (purchase-flow-step-data)
  const stepData = getValue("purchase-flow-step-data") || {}
  const address = stepData.billing?.address || null
  // a parcel collected at an office or locker goes there, not to the address
  const pickupOffice = stepData.delivery?.option?.office || null

  const lineCount = cart.reduce((sum, line) => sum + (line.quantity || 1), 0)
  const sellers = groups.map((group) => group.name).filter(Boolean)

  // No item list here on purpose: a cart can hold many lines and the payment
  // step is about the payment method. The cart panel lists what is bought,
  // this is the compact recap (fixed height whatever the cart size).
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-neutral p-4 mb-5" data-testid="checkout-summary">
      <div className="flex items-center justify-between gap-3 text-[14px]">
        <span data-testid="summary-items">
          {lineCount} {lineCount === 1 ? t`item` : t`items`}
        </span>
        {sellers.length > 0 && (
          <span className="flex items-center gap-2 min-w-0 text-[13px] text-text_secondary" data-testid="merchant-info">
            <BuildingStorefrontIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">
              {t`Sold by`}: <span className="text-text" data-testid="merchant-name">{sellers[0]}</span>
              {sellers.length > 1 && ` +${sellers.length - 1}`}
            </span>
          </span>
        )}
      </div>
      {shippingCents > 0 && (
        <div className="flex justify-between text-[14px] text-text_secondary">
          <span>{t`Shipping`}</span>
          <span data-testid="summary-shipping">{formatMoney(shippingCents, currency)}</span>
        </div>
      )}
      <div className="flex justify-between border-t border-bordered pt-2 text-[16px] font-bold">
        <span>{t`Total`}</span>
        <span data-testid="checkout-total">{formatMoney(totalCents, currency)}</span>
      </div>
      {pickupOffice && (
        <div className="text-[13px] text-text_secondary" data-testid="summary-office">
          {pickupOffice.type === "shop" ? t`Collect from the shop` : stepData.delivery.option.method === "locker" ? t`Collect from locker` : t`Collect from office`}: {[pickupOffice.type === "shop" ? null : stepData.delivery.option.providerName, pickupOffice.name, pickupOffice.address].filter(Boolean).join(", ")}
        </div>
      )}
      {address && !pickupOffice && (
        <div className="text-[13px] text-text_secondary" data-testid="summary-address">
          {t`Delivery address`}: {[address.name, address.line1, address.city, address.postal_code].filter(Boolean).join(", ")}
        </div>
      )}
    </div>
  )
}
